export type LabelTemplateTarget = 'finca' | 'suerte' | 'tablon' | 'fragment' | 'selection';
export type LabelTargetSource = 'scope' | 'theme' | 'theme-valued' | 'theme-categories' | 'selection' | 'saved-selection' | 'custom' | 'none';

/** Serializable label subset, independent from DISPLAY SET and THEME TARGET. */
export interface LabelTarget {
  readonly source: LabelTargetSource;
  readonly scope: WorkScope;
  readonly featureIds: readonly string[];
  readonly referenceId?: string;
  /** Source category values, never presentation aliases. Only used by theme-categories. */
  readonly categoryValues?: readonly string[];
}

export interface LabelConfig {
  /** Entity/template currently being edited; it does not define the label subset. */
  readonly target: LabelTemplateTarget;
  readonly labelTarget: LabelTarget;
  readonly templates: Readonly<Record<LabelTemplateTarget, string>>;
  readonly showArea: boolean;
  readonly numericFormats: Readonly<Record<string, LabelNumericFormat>>;
  readonly style: LabelTextStyleConfig;
  readonly placement: LabelPlacementConfig;
  /** Absolute EPSG:3857 anchor overrides keyed by logical entity id. */
  readonly positionOverrides: Readonly<Record<string, LabelPositionOverride>>;
}

export type LabelDecimals = 'auto' | 0 | 1 | 2 | 3 | 4;
export interface LabelNumericFormat { readonly decimals: LabelDecimals; readonly prefix: string; readonly suffix: string; readonly unit: string }
export interface LabelTextStyleConfig {
  readonly fontSize: number;
  readonly bold: boolean;
  readonly textColor: string;
  readonly halo: boolean;
  readonly haloColor: string;
  readonly haloWidth: number;
  readonly background: boolean;
  readonly backgroundColor: string;
  readonly backgroundOpacity: number;
  readonly padding: number;
  /** Preserves the historical satellite contrast until the user chooses explicit colors. */
  readonly contrastMode: 'automatic' | 'fixed';
}
export type LabelAutomaticPosition = 'center' | 'top' | 'bottom' | 'left' | 'right';
export interface LabelPlacementConfig { readonly position: LabelAutomaticPosition; readonly offsetX: number; readonly offsetY: number }
export interface LabelPositionOverride { readonly coordinate: readonly [number, number] }

export interface StyleConfig {
  readonly fillColor: string;
  readonly outlineColor: string;
  readonly fillOpacity: number;
  readonly outlineWidth: number;
  readonly selectionFillColor: string;
  readonly selectionOutlineColor: string;
  readonly selectionOutlineWidth: number;
  readonly dimOpacity: number;
  readonly showPhysicalFragments: boolean;
}

export interface CategoryStyle {
  readonly id?: string;
  readonly value: string;
  readonly label: string;
  readonly color: string;
  readonly visible: boolean;
  readonly min?: number;
  readonly max?: number;
  /** V1.9.0: category created for a scenario (e.g. «PV»): kept when the categories are rebuilt from the data. */
  readonly proposed?: boolean;
}

export type ClassificationPaint = 'fill' | 'outline' | 'fill-outline';
export type ClassificationLevel = 'finca' | 'suerte' | 'tablon' | 'fragment' | 'feature';
export type ThemeTargetSource = 'scope' | 'selection' | 'saved-selection' | 'filter' | 'custom' | 'layer';
export type RemainderStyle = 'base' | 'dim' | 'hide';
export type ThematicViewMode = 'classified-only' | 'context' | 'all';
export interface ThemeTargetConfig {
  readonly source: ThemeTargetSource;
  readonly scope: WorkScope;
  /** Snapshot of physical feature ids. It keeps map selections stable after the user selects something else. */
  readonly featureIds: readonly string[];
  readonly referenceId?: string;
}
export type ThematicMode = 'single' | 'categorized' | 'graduated';
export type GraduatedMethod = 'quantile' | 'equal' | 'manual';
export interface ThematicSingleStyle { readonly fillColor: string; readonly outlineColor: string; readonly fillOpacity: number; readonly outlineWidth: number }

export interface ClassificationConfig {
  readonly id: string;
  readonly name: string;
  readonly layerId: string | null;
  readonly themeTarget: ThemeTargetConfig;
  readonly level: ClassificationLevel;
  readonly remainderStyle: RemainderStyle;
  /** Controls presentation of DISPLAY SET entities outside the thematic value set. */
  readonly thematicView: ThematicViewMode;
  /** Independent from thematicView: controls entities whose selected field has no value. */
  readonly showNoValue: boolean;
  /** Presentation color for DISPLAY SET features outside THEME TARGET. */
  readonly remainderColor: string;
  readonly mode: ThematicMode;
  readonly field: string | null;
  readonly categories: readonly CategoryStyle[];
  readonly method: GraduatedMethod;
  readonly classCount: number;
  readonly manualBreaks: readonly number[];
  readonly palette: readonly string[];
  readonly reversePalette: boolean;
  readonly opacity: number;
  readonly paint: ClassificationPaint;
  readonly singleStyle: ThematicSingleStyle;
  readonly legend: LegendConfig;
  /** Áreas parciales as thematic exceptions of a categorized Suerte classification. Absent = off (older projects). */
  readonly partialExceptions?: PartialExceptionsConfig;
  /**
   * V1.8.1 «Desglosar categorías»: explicit table that splits the values of ONE combined field into two parts. It is a
   * presentation definition only: the source field, its records and the Excel are never rewritten. Absent = none.
   */
  readonly categorySplit?: CategorySplitConfig;
  /** The principal classification reads this part of `categorySplit` instead of the raw field. Absent = raw field. */
  readonly splitPart?: SplitPart;
  /** V1.8.1 secondary classification (outline) on the same entities. Absent = the simple classification, unchanged. */
  readonly secondary?: SecondaryClassificationConfig;
}

export type SplitPart = 'primary' | 'secondary';

/** One source value and its two parts; an empty part means «Sin valor» in that dimension. */
export interface CategorySplitRow { readonly value: string; readonly primary: string; readonly secondary: string }

export interface CategorySplitConfig {
  /** Field whose values are split (any field of the catalog: Maestro, Excel, Proyecto). */
  readonly sourceField: string;
  readonly primaryName: string;
  readonly secondaryName: string;
  /** Exact source values only: nothing is inferred from separators; a value absent here has no value in both parts. */
  readonly rows: readonly CategorySplitRow[];
}

export type SecondaryLineStyle = 'solid' | 'dashed';

export interface SecondaryClassificationConfig {
  /** Off keeps the definition (field, colours, aliases) to turn it on again. */
  readonly enabled: boolean;
  readonly field: string | null;
  /** Reads this part of the classification's `categorySplit` instead of the raw field. */
  readonly splitPart?: SplitPart;
  /** Title of its legend. */
  readonly name: string;
  /** Outline colour and alias per category (same shape as the principal categories). */
  readonly categories: readonly CategoryStyle[];
  readonly line: SecondaryLineStyle;
  /** Outline width in screen pixels (scaled once by the PNG render scale). */
  readonly width: number;
}

export interface PartialExceptionsConfig {
  readonly enabled: boolean;
  /** Project field of level Área parcial whose values override the Suerte value inside each subarea. */
  readonly fieldId: string | null;
}

export type LegendPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'free';

export interface LegendConfig {
  readonly visible: boolean;
  readonly title: string;
  readonly showCount: boolean;
  readonly showArea: boolean;
  readonly areaUnit: 'ha' | 'mz';
  readonly position: LegendPosition;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly scale: number;
  readonly fontSize: number;
  readonly swatchSize: number;
  readonly rowGap: number;
  readonly columnGap: number;
  readonly metricsAlignment: 'start' | 'end';
  readonly padding: number;
  readonly backgroundColor: string;
  readonly borderColor: string;
  readonly backgroundOpacity: number;
  readonly decimals: number;
  readonly showCategories: boolean;
  readonly includeRemainder: boolean;
  readonly remainderLabel: string;
  /** V1.9.0: typeface of the legend (screen and PNG). */
  readonly fontFamily: LegendFontFamily;
  /** V1.9.0: border width in px (0 = no border). */
  readonly borderWidth: number;
  /** V1.9.0: legend order of the principal / secondary category values (absent = the categories' own order). */
  readonly categoryOrder?: readonly string[];
  readonly secondaryOrder?: readonly string[];
  /** V1.9.0 E3: 'grouped' = secondary (B) → principal (A) with subtotals; absent = 'flat' (the V1.8.1 legend). */
  readonly layout?: LegendLayout;
  /** Grouped legend: subtotal of every group (absent = shown). */
  readonly showSubtotals?: boolean;
  /** Grouped legend: total line (absent = shown). */
  readonly showTotal?: boolean;
  /** Entities without a secondary (B) value: neutral «Sin ‹B›» group / row (absent = shown; never silently dropped). */
  readonly showUnassigned?: boolean;
}

export type LegendLayout = 'flat' | 'grouped';

export type LegendFontFamily = 'system' | 'serif' | 'condensed';

/** The one definition of the legend defaults (hoisted: the default classification embeds a copy). */
function defaultLegendConfigValue(): LegendConfig {
  return {
    visible: true,
    title: 'Clasificación',
    showCount: true,
    showArea: false,
    areaUnit: 'ha',
    position: 'bottom-right',
    x: 12,
    y: 58,
    width: 230,
    scale: 1,
    fontSize: 10,
    swatchSize: 16,
    rowGap: 4,
    columnGap: 6,
    metricsAlignment: 'end',
    padding: 10,
    backgroundColor: '#ffffff',
    borderColor: '#1f4737',
    backgroundOpacity: 0.94,
    decimals: 1,
    showCategories: true,
    includeRemainder: false,
    remainderLabel: 'Resto visible',
    fontFamily: 'system',
    borderWidth: 1,
  };
}

export const defaultLabelConfig: LabelConfig = {
  target: 'suerte',
  labelTarget: { source: 'scope', scope: { fincaIds: [], suerteIds: [], tablonIds: [] }, featureIds: [] },
  templates: {
    finca: '{system:logical:finca:name}',
    suerte: 'Suerte {system:logical:suerte:label}',
    tablon: '{system:logical:tablon:code}',
    fragment: '{system:logical:fragment:id}',
    selection: '{Nombre}',
  },
  showArea: false,
  numericFormats: {},
  style: { fontSize: 11, bold: true, textColor: '#143a2d', halo: true, haloColor: '#ffffff', haloWidth: 2.6, background: true, backgroundColor: '#ffffff', backgroundOpacity: 0.62, padding: 3, contrastMode: 'automatic' },
  placement: { position: 'center', offsetX: 0, offsetY: 0 },
  positionOverrides: {},
};

export const SIMPLE_ENTITY_LABEL_FIELDS: Readonly<Record<Exclude<LabelTemplateTarget, 'selection'>, { readonly fieldId: string; readonly label: string; readonly template: string }>> = {
  finca: { fieldId: 'system:logical:finca:name', label: 'Nombre de finca', template: '{system:logical:finca:name}' },
  suerte: { fieldId: 'system:logical:suerte:label', label: 'Suerte', template: 'Suerte {system:logical:suerte:label}' },
  tablon: { fieldId: 'system:logical:tablon:code', label: 'Tablón', template: '{system:logical:tablon:code}' },
  fragment: { fieldId: 'system:logical:fragment:id', label: 'Fragmento', template: '{system:logical:fragment:id}' },
};

/** Aligns normal labeling with EntityView and migrates only the unsafe one-token physical-field legacy case. */
export function alignLabelConfigToEntity(config: LabelConfig, target: Exclude<LabelTemplateTarget, 'selection'>): LabelConfig {
  const current = config.templates[target];
  const unsafeLegacySource = /^\s*\{source:[^{}]+\}\s*$/.test(current);
  return {
    ...config,
    target,
    templates: unsafeLegacySource ? { ...config.templates, [target]: SIMPLE_ENTITY_LABEL_FIELDS[target].template } : config.templates,
  };
}

/** Default applied only to a newly created/imported Maestro CASUR. */
export const DEFAULT_MASTER_STYLE: StyleConfig = {
  fillColor: '#439163',
  outlineColor: '#ffdf00',
  fillOpacity: 0.15,
  outlineWidth: 1.5,
  selectionFillColor: '#f4b134',
  selectionOutlineColor: '#9b5700',
  selectionOutlineWidth: 2.75,
  dimOpacity: 0.05,
  showPhysicalFragments: false,
};

export const defaultLegendConfig: LegendConfig = defaultLegendConfigValue();

export const defaultClassificationConfig: ClassificationConfig = {
  id: 'thematic-default',
  name: 'Sin clasificación',
  layerId: null,
  themeTarget: { source: 'scope', scope: { fincaIds: [], suerteIds: [], tablonIds: [] }, featureIds: [] },
  level: 'suerte',
  remainderStyle: 'base',
  thematicView: 'all',
  showNoValue: true,
  remainderColor: DEFAULT_MASTER_STYLE.fillColor,
  mode: 'single',
  field: null,
  categories: [],
  method: 'quantile',
  classCount: 5,
  manualBreaks: [],
  palette: ['#2a9d8f', '#e9c46a', '#f4a261', '#e76f51', '#457b9d'],
  reversePalette: false,
  opacity: 0.45,
  paint: 'fill-outline',
  singleStyle: { fillColor: DEFAULT_MASTER_STYLE.fillColor, outlineColor: DEFAULT_MASTER_STYLE.outlineColor, fillOpacity: DEFAULT_MASTER_STYLE.fillOpacity, outlineWidth: DEFAULT_MASTER_STYLE.outlineWidth },
  legend: defaultLegendConfig,
};


export const updateLabelTemplate = (config: LabelConfig, target: LabelTemplateTarget, template: string): LabelConfig => ({
  ...config,
  templates: { ...config.templates, [target]: template },
});

export const colorWithOpacity = (hex: string, opacity: number): string => {
  const normalized = hex.replace('#', '');
  const expanded = normalized.length === 3 ? normalized.split('').map((item) => item + item).join('') : normalized;
  const parsed = Number.parseInt(expanded, 16);
  if (!Number.isFinite(parsed) || expanded.length !== 6) return `rgba(67,145,99,${opacity})`;
  return `rgba(${(parsed >> 16) & 255},${(parsed >> 8) & 255},${parsed & 255},${opacity})`;
};
import type { WorkScope } from '../core/types';
